// BILLING-001 Phase 7a client (P7-A). Shapes mirror packages/domain/billing/src/wallet-http.ts and the
// summary in packages/domain/billing/src/http.ts.
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { request } from "../api/client";
import type { BillingSummary } from "../api/types";

const org = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}`;

/** A dependency that is not activated yet (truthful state from the server). */
export interface GatewayState {
  available: false;
  state: "blocked: activation required";
  dependency: string;
}

export interface WalletView {
  wallet: {
    currency: "NPR";
    balance_paisa: number;
    reserved_paisa: number;
    available_paisa: number;
    low_threshold_paisa: number | null;
    low: boolean;
  };
  topup: GatewayState;
}

export type WalletEntryKind = "topup" | "debit" | "reserve" | "release" | "refund" | "mission";

export interface LedgerEntry {
  id: string;
  kind: WalletEntryKind;
  amount_paisa: number;
  balance_change_paisa: number;
  ref_type: string;
  ref_id: string;
  requested_paisa: number | null;
  balance_after_paisa: number;
  reserved_after_paisa: number;
  available_after_paisa: number;
  op: "reserve" | "settle" | "release" | "sweep" | "credit";
  actor_type: string;
  note: string | null;
  created_at: string;
}

export interface Invoice {
  id: string;
  number: string;
  amount_paisa: number;
  currency: "NPR";
  state: "draft" | "open" | "paid" | "void" | "refunded";
  paid_via: string | null;
  plan_id: string | null;
  period: string | null;
  issued_at: string;
}

export interface AddonsView {
  offers: {
    key: string;
    quota_key: string;
    increment: number;
    price: Record<string, unknown> | null;
    purchasable: boolean;
  }[];
  active: { id: string; offer_key: string | null; quota_key: string; quantity: number }[];
  quota_increments: Record<string, number>;
  purchase: GatewayState;
}

export interface CreditsView {
  data: {
    id: string;
    kind: "credit" | "refund";
    amount_paisa: number;
    reason: string;
    state: string;
    created_at: string;
  }[];
  refund_policy: { window_days: number | null; self_serve: boolean };
}

export type TrialBlock = "no_trial_plan" | "trial_used" | "already_subscribed";

/** GET /orgs/{o}/billing with the Phase 7a fields (older servers omit them). */
export interface BillingSummary7 extends BillingSummary {
  subscription:
    | (NonNullable<BillingSummary["subscription"]> & {
        kind?: "base" | "trial" | "paid";
        grace_ends_at?: string | null;
      })
    | null;
  trial?: {
    available: boolean;
    reason: TrialBlock | null;
    plans: { id: string; key: string; name: string; trial_days: number }[];
    current: {
      id: string;
      state: string;
      plan: { id: string; key: string };
      ends_at: string | null;
      auto_converts: false;
    } | null;
  };
  checkout?: GatewayState;
}

export const walletApi = {
  wallet: (o: string) => request<WalletView>("GET", `${org(o)}/wallet`),
  ledger: (o: string, cursor?: string, limit = 25) =>
    request<{ data: LedgerEntry[]; next_cursor: string | null }>("GET", `${org(o)}/wallet/ledger`, {
      query: { cursor, limit },
    }),
  invoices: (o: string) =>
    request<{ data: Invoice[]; next_cursor: string | null }>("GET", `${org(o)}/billing/invoices`),
  addons: (o: string) => request<AddonsView>("GET", `${org(o)}/billing/addons`),
  credits: (o: string) => request<CreditsView>("GET", `${org(o)}/billing/credits`),
  startTrial: (o: string, planId?: string) =>
    request<{ subscription: { id: string; state: string; ends_at: string } }>(
      "POST",
      `${org(o)}/trial`,
      {
        body: planId ? { plan_id: planId } : {},
        idempotent: true,
      },
    ),
};

export const walletKeys = {
  wallet: (o: string) => ["wallet", o, "wallet"] as const,
  ledger: (o: string) => ["wallet", o, "ledger"] as const,
  invoices: (o: string) => ["wallet", o, "invoices"] as const,
  addons: (o: string) => ["wallet", o, "addons"] as const,
  credits: (o: string) => ["wallet", o, "credits"] as const,
};

export const useWallet = (o: string, enabled = true) =>
  useQuery({ queryKey: walletKeys.wallet(o), queryFn: () => walletApi.wallet(o), enabled });
export const useInvoices = (o: string, enabled = true) =>
  useQuery({ queryKey: walletKeys.invoices(o), queryFn: () => walletApi.invoices(o), enabled });
export const useAddons = (o: string, enabled = true) =>
  useQuery({ queryKey: walletKeys.addons(o), queryFn: () => walletApi.addons(o), enabled });
export const useCredits = (o: string, enabled = true) =>
  useQuery({ queryKey: walletKeys.credits(o), queryFn: () => walletApi.credits(o), enabled });
export const LEDGER_PAGE = 25;
export const useLedger = (o: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: walletKeys.ledger(o),
    queryFn: ({ pageParam }) => walletApi.ledger(o, pageParam, LEDGER_PAGE),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  });
